import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import ChatbotAPI from './ChatbotAPI';
import FlowExecutionEngine from './FlowExecutionEngine';
import { ErrorToastContainer, SuccessToastContainer } from '../../../function/CustomFunction';

export default function EnhancedInteractiveChatBot() {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [flows, setFlows] = useState([]);
  const [selectedFlow, setSelectedFlow] = useState(null);
  const [showFlowSelector, setShowFlowSelector] = useState(false);
  const [conversationState, setConversationState] = useState(null);
  const [customerData, setCustomerData] = useState({
    name: '',
    phone: '',
    email: ''
  });
  const [showCustomerForm, setShowCustomerForm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [flowExecutionEngine, setFlowExecutionEngine] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [testSettings, setTestSettings] = useState({
    autoReply: true,
    typingDelay: 1000,
    messageDelay: 500
  });
  
  const messagesEndRef = useRef(null);
  const Accesstoken = useSelector((state) => state.Token.value);

  // Initialize the chatbot
  useEffect(() => {
    initializeChatbot();
  }, []);

  const initializeChatbot = async () => {
    try {
      setIsLoading(true);
      
      // Load available flows
      const flowsData = await ChatbotAPI.loadFlows();
      setFlows(flowsData);
      
      // Initialize flow execution engine
      const engine = new FlowExecutionEngine();
      setFlowExecutionEngine(engine);
      
      // Add welcome message
      addMessage({
        id: Date.now(),
        type: 'bot',
        content: 'Welcome to the Chatbot Tester! Select a flow to start testing.',
        timestamp: new Date()
      });
      
    } catch (error) {
      console.error('Error initializing chatbot:', error);
      ErrorToastContainer('Failed to initialize chatbot');
    } finally {
      setIsLoading(false);
    }
  };

  // Add message to chat
  const addMessage = (message) => {
    setMessages(prev => [...prev, message]);
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  // Start conversation with selected flow
  const startConversation = async (flow) => {
    try {
      setIsLoading(true);
      setSelectedFlow(flow);
      setIsConnected(true);
      
      // Initialize conversation with flow execution engine
      const conversation = await flowExecutionEngine.initializeConversation(flow.id, customerData);
      setConversationId(conversation.id);
      setConversationState(conversation);
      
      // Add flow start message
      addMessage({
        id: Date.now(),
        type: 'bot',
        content: `🚀 Started testing flow: ${flow.name}`,
        timestamp: new Date()
      });
      
      // Execute first node
      if (conversation.firstMessage) {
        addMessage({
          id: Date.now() + 1,
          type: 'bot',
          content: conversation.firstMessage,
          timestamp: new Date(),
          buttons: conversation.buttons || []
        });
      }
      
    } catch (error) {
      console.error('Error starting conversation:', error);
      ErrorToastContainer('Failed to start conversation');
    } finally {
      setIsLoading(false);
    }
  };

  // Send message and get bot response
  const sendMessage = async () => {
    if (!inputMessage.trim() || !isConnected || !flowExecutionEngine) return;
    
    const userMessage = inputMessage.trim();
    setInputMessage('');
    
    // Add user message
    addMessage({
      id: Date.now(),
      type: 'user',
      content: userMessage,
      timestamp: new Date()
    });
    
    // Show typing indicator
    setIsTyping(true);
    
    try {
      // Send message to flow execution engine
      const response = await flowExecutionEngine.sendMessage(conversationId, userMessage);
      
      // Simulate typing delay
      await new Promise(resolve => setTimeout(resolve, testSettings.typingDelay));
      
      // Add bot response
      if (response.message) {
        addMessage({
          id: Date.now() + 1,
          type: 'bot',
          content: response.message,
          timestamp: new Date(),
          buttons: response.buttons || [],
          media: response.media || null
        });
      }
      
      // Update conversation state
      setConversationState(response.conversationState);
      
    } catch (error) {
      console.error('Error sending message:', error);
      addMessage({
        id: Date.now() + 1,
        type: 'bot',
        content: 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date()
      });
    } finally {
      setIsTyping(false);
    }
  };

  // Handle button clicks
  const handleButtonClick = async (button) => {
    if (!isConnected || !flowExecutionEngine) return;
    
    // Add button click as user message
    addMessage({
      id: Date.now(),
      type: 'user',
      content: button.label,
      timestamp: new Date(),
      isButton: true
    });
    
    // Show typing indicator
    setIsTyping(true);
    
    try {
      // Process button click
      const response = await flowExecutionEngine.processButtonClick(conversationId, button);
      
      // Simulate typing delay
      await new Promise(resolve => setTimeout(resolve, testSettings.typingDelay));
      
      // Add bot response
      if (response.message) {
        addMessage({
          id: Date.now() + 1,
          type: 'bot',
          content: response.message,
          timestamp: new Date(),
          buttons: response.buttons || [],
          media: response.media || null
        });
      }
      
      // Update conversation state
      setConversationState(response.conversationState);
      
    } catch (error) {
      console.error('Error processing button click:', error);
      addMessage({
        id: Date.now() + 1,
        type: 'bot',
        content: 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date()
      });
    } finally {
      setIsTyping(false);
    }
  };

  // Reset conversation
  const resetConversation = () => {
    setMessages([]);
    setSelectedFlow(null);
    setIsConnected(false);
    setConversationId(null);
    setConversationState(null);
    
    // Add welcome message
    addMessage({
      id: Date.now(),
      type: 'bot',
      content: 'Conversation reset. Select a flow to start testing.',
      timestamp: new Date()
    });
  };

  // WhatsApp-style bubble for bot messages
  const botBubbleStyle = {
    background: '#e5fff9',
    border: '1px solid #b2dfdb',
    borderRadius: '0 12px 12px 12px',
    padding: '12px 16px',
    maxWidth: '280px',
    fontFamily: 'Segoe UI, Arial, sans-serif',
    fontSize: '15px',
    color: '#222',
    boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
    margin: '8px 0',
    display: 'inline-block',
    textAlign: 'left',
    position: 'relative',
    wordBreak: 'break-word',
    lineHeight: 1.4,
  };

  // WhatsApp-style bubble for user messages
  const userBubbleStyle = {
    background: '#dcf8c6',
    border: '1px solid #b2dfdb',
    borderRadius: '12px 12px 0 12px',
    padding: '12px 16px',
    maxWidth: '280px',
    fontFamily: 'Segoe UI, Arial, sans-serif',
    fontSize: '15px',
    color: '#222',
    boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
    margin: '8px 0',
    display: 'inline-block',
    textAlign: 'right',
    position: 'relative',
    wordBreak: 'break-word',
    lineHeight: 1.4,
  };

  // Quick reply button style
  const quickReplyStyle = {
    background: '#f0f0f0',
    border: '1px solid #ddd',
    borderRadius: '8px',
    padding: '8px 16px',
    margin: '4px',
    fontSize: '14px',
    color: '#333',
    cursor: 'pointer',
    display: 'inline-block',
    transition: 'all 0.2s',
  };

  // Load available flows
  const loadFlows = async () => {
    setIsLoading(true);
    try {
      const flowsData = await ChatbotAPI.loadFlows();
      setFlows(flowsData.filter(flow => flow.is_active));
    } catch (error) {
      ErrorToastContainer('Failed to load flows');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadFlows();
  }, []);

  // Auto-scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);



  // Handle quick reply
  const handleQuickReply = (buttonText) => {
    setInputMessage(buttonText);
    // Auto-send the quick reply
    setTimeout(() => {
      sendMessage();
    }, 100);
  };

  // Handle key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };


  // Render message content based on type
  const renderMessageContent = (message) => {
    return (
      <div>
        <div>{message.text}</div>
        
        {/* Media content */}
        {message.mediaUrl && (
          <div style={{ marginTop: 8 }}>
            {message.mediaType === 'image' ? (
              <img 
                src={message.mediaUrl} 
                alt="Media" 
                style={{ maxWidth: '100%', borderRadius: 8 }}
              />
            ) : message.mediaType === 'video' ? (
              <video 
                src={message.mediaUrl} 
                controls 
                style={{ maxWidth: '100%', borderRadius: 8 }}
              />
            ) : (
              <div style={{ 
                padding: 8, 
                background: '#f0f0f0', 
                borderRadius: 4,
                fontSize: 12 
              }}>
                📎 Document: {message.mediaUrl.split('/').pop()}
              </div>
            )}
          </div>
        )}

        {/* Product content */}
        {message.product && (
          <div style={{ 
            marginTop: 8, 
            border: '1px solid #ddd', 
            borderRadius: 8, 
            padding: 12,
            background: '#fff'
          }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>
              {message.product.name}
            </div>
            <div style={{ color: '#25d366', fontWeight: 600, marginBottom: 4 }}>
              {message.product.price}
            </div>
            {message.product.description && (
              <div style={{ fontSize: 14, color: '#666' }}>
                {message.product.description}
              </div>
            )}
          </div>
        )}

        {/* List content */}
        {message.listItems && message.listItems.length > 0 && (
          <div style={{ marginTop: 8 }}>
            {message.listItems.map((item, index) => (
              <div key={index} style={{ 
                padding: '8px 12px', 
                background: '#f0f0f0', 
                marginBottom: 4, 
                borderRadius: 4,
                cursor: 'pointer'
              }}>
                {item}
              </div>
            ))}
          </div>
        )}

        {/* Quick reply buttons */}
        {message.buttons && message.buttons.length > 0 && (
          <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {message.buttons.map((button, index) => (
              <button
                key={index}
                onClick={() => handleQuickReply(button.label)}
                style={quickReplyStyle}
                onMouseOver={e => e.currentTarget.style.background = '#e0e0e0'}
                onMouseOut={e => e.currentTarget.style.background = '#f0f0f0'}
              >
                {button.label}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ 
        background: '#fff', 
        borderBottom: '1px solid #e0e0e0', 
        padding: '16px 24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600 }}>
            Interactive ChatBot
          </h2>
          <div style={{ fontSize: 14, color: '#666', marginTop: 4 }}>
            {conversationState ? 'Conversation Active' : 'Select a flow to start'}
          </div>
        </div>
        
        <div style={{ display: 'flex', gap: 8 }}>
          {!conversationState && (
            <button
              onClick={() => setShowCustomerForm(true)}
              style={{
                padding: '8px 16px',
                background: '#25d366',
                color: 'white',
                border: 'none',
                borderRadius: 4,
                cursor: 'pointer'
              }}
            >
              Start New Chat
            </button>
          )}
          {conversationState && (
            <button
              onClick={resetConversation}
              style={{
                padding: '8px 16px',
                background: '#ff4444',
                color: 'white',
                border: 'none',
                borderRadius: 4,
                cursor: 'pointer'
              }}
            >
              End Chat
            </button>
          )}
        </div>
      </div>

      {/* Chat area */}
      <div style={{ 
        flex: 1, 
        background: '#f0f0f0', 
        padding: '16px',
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {messages.length === 0 ? (
          <div style={{ 
            textAlign: 'center', 
            padding: 40, 
            color: '#666',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>🤖</div>
            <div style={{ fontSize: 18, marginBottom: 8 }}>Welcome to the ChatBot!</div>
            <div style={{ fontSize: 14, color: '#888' }}>
              Select a flow and start a conversation to test your chatbot.
            </div>
          </div>
        ) : (
          <div style={{ flex: 1 }}>
            {messages.map((message) => (
              <div
                key={message.id}
                style={{
                  display: 'flex',
                  justifyContent: message.sender === 'user' ? 'flex-end' : 'flex-start',
                  marginBottom: 8,
                }}
              >
                <div style={message.sender === 'user' ? userBubbleStyle : botBubbleStyle}>
                  {renderMessageContent(message)}
                </div>
              </div>
            ))}
            
            {isTyping && (
              <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 8 }}>
                <div style={botBubbleStyle}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <div style={{ fontSize: 12 }}>Bot is typing</div>
                    <div style={{ display: 'flex', gap: 2 }}>
                      <div style={{ 
                        width: 4, 
                        height: 4, 
                        background: '#666', 
                        borderRadius: '50%', 
                        animation: 'typing 1.4s infinite ease-in-out' 
                      }}></div>
                      <div style={{ 
                        width: 4, 
                        height: 4, 
                        background: '#666', 
                        borderRadius: '50%', 
                        animation: 'typing 1.4s infinite ease-in-out 0.2s' 
                      }}></div>
                      <div style={{ 
                        width: 4, 
                        height: 4, 
                        background: '#666', 
                        borderRadius: '50%', 
                        animation: 'typing 1.4s infinite ease-in-out 0.4s' 
                      }}></div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input area */}
      {conversationState && (
        <div style={{ 
          background: '#fff', 
          borderTop: '1px solid #e0e0e0', 
          padding: '16px 24px' 
        }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
            <textarea
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type your message..."
              style={{
                flex: 1,
                minHeight: 40,
                maxHeight: 120,
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: 8,
                fontSize: 14,
                resize: 'none',
                fontFamily: 'inherit'
              }}
            />
            <button
              onClick={sendMessage}
              disabled={!inputMessage.trim()}
              style={{
                padding: '8px 16px',
                background: inputMessage.trim() ? '#25d366' : '#ccc',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                cursor: inputMessage.trim() ? 'pointer' : 'not-allowed',
                fontSize: 14,
                fontWeight: 500
              }}
            >
              Send
            </button>
          </div>
        </div>
      )}

      {/* Customer Form Modal */}
      {showCustomerForm && (
        <div style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          background: 'rgba(0,0,0,0.5)', 
          zIndex: 10000, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center' 
        }}>
          <div style={{ 
            background: '#fff', 
            borderRadius: 12, 
            padding: 24, 
            minWidth: 400, 
            maxWidth: 500, 
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)' 
          }}>
            <div style={{ 
              fontWeight: 600, 
              fontSize: 18, 
              marginBottom: 16 
            }}>
              Customer Information
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: 500 }}>
                  Name *
                </label>
                <input
                  type="text"
                  value={customerData.name}
                  onChange={(e) => setCustomerData(prev => ({ ...prev, name: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: 4,
                    fontSize: 14
                  }}
                  placeholder="Enter customer name"
                />
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: 500 }}>
                  Phone Number *
                </label>
                <input
                  type="tel"
                  value={customerData.phone}
                  onChange={(e) => setCustomerData(prev => ({ ...prev, phone: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: 4,
                    fontSize: 14
                  }}
                  placeholder="Enter phone number"
                />
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: 500 }}>
                  Email
                </label>
                <input
                  type="email"
                  value={customerData.email}
                  onChange={(e) => setCustomerData(prev => ({ ...prev, email: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: 4,
                    fontSize: 14
                  }}
                  placeholder="Enter email (optional)"
                />
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
              <button
                onClick={() => setShowCustomerForm(false)}
                style={{
                  flex: 1,
                  padding: '8px 16px',
                  background: '#f0f0f0',
                  border: 'none',
                  borderRadius: 4,
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowCustomerForm(false);
                  setShowFlowSelector(true);
                }}
                disabled={!customerData.name || !customerData.phone}
                style={{
                  flex: 1,
                  padding: '8px 16px',
                  background: customerData.name && customerData.phone ? '#25d366' : '#ccc',
                  color: 'white',
                  border: 'none',
                  borderRadius: 4,
                  cursor: customerData.name && customerData.phone ? 'pointer' : 'not-allowed'
                }}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Flow Selector Modal */}
      {showFlowSelector && (
        <div style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          background: 'rgba(0,0,0,0.5)', 
          zIndex: 10000, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center' 
        }}>
          <div style={{ 
            background: '#fff', 
            borderRadius: 12, 
            padding: 24, 
            minWidth: 500, 
            maxWidth: 600, 
            maxHeight: 600, 
            overflowY: 'auto', 
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)' 
          }}>
            <div style={{ 
              fontWeight: 600, 
              fontSize: 18, 
              marginBottom: 16 
            }}>
              Select a Flow
            </div>
            
            {flows.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: '#666' }}>
                No active flows found. Please create a flow first.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {flows.map(flow => (
                  <div 
                    key={flow.id} 
                    style={{ 
                      border: '1px solid #e0e0e0', 
                      borderRadius: 8, 
                      padding: 16, 
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onClick={() => startConversation(flow)}
                    onMouseOver={e => e.currentTarget.style.background = '#f0f7fa'}
                    onMouseOut={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 4 }}>
                      {flow.name}
                    </div>
                    <div style={{ fontSize: 14, color: '#666', marginBottom: 4 }}>
                      {flow.description || 'No description'}
                    </div>
                    <div style={{ fontSize: 12, color: '#888' }}>
                      Created: {new Date(flow.created_at).toLocaleDateString()}
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            <button
              onClick={() => setShowFlowSelector(false)}
              style={{
                marginTop: 16,
                padding: '8px 16px',
                background: '#f0f0f0',
                border: 'none',
                borderRadius: 4,
                cursor: 'pointer',
                width: '100%'
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* CSS for typing animation */}
      <style>{`
        @keyframes typing {
          0%, 60%, 100% { transform: translateY(0); }
          30% { transform: translateY(-10px); }
        }
      `}</style>
    </div>
  );
} 